import { ToQueryKey } from './ThinkAlphaQueryClient';
import type { QueryKey } from './ThinkAlphaQueryClient';
import { Duration } from '@js-joda/core';
import { QueryClient } from '@tanstack/query-core';
import type { QueryClientConfig } from '@tanstack/query-core';
import { AxiosError } from 'axios';
import { injectable } from 'src/features/ioc';

const JITTER_MS = 5000;

export function mapToQueryKey(key: unknown | QueryKey): unknown {
    if (key && typeof key === 'object' && ToQueryKey in key) {
        return (key as QueryKey)[ToQueryKey]();
    }
    return key;
}

/**
 * App-wide retry policy: retry transport-class failures (network, 5xx, 429)
 * indefinitely with the backoff below; never retry cancellations, other 4xx,
 * or non-Axios (semantic) errors. Exported so individual queries can compose
 * with it when they also want semantic failures retried.
 */
export function shouldRetryTransportError(_failureCount: number, error: Error): boolean {
    if (error instanceof AxiosError) {
        if (error.code === 'ERR_CANCELED') {
            return false;
        }

        const status = error.response?.status;
        if (status !== undefined && status >= 400 && status < 500 && status !== 429) {
            return false;
        }
        return true;
    }
    return false;
}

export const QueryClientOptions: QueryClientConfig = {
    defaultOptions: {
        queries: {
            refetchOnReconnect: 'always',
            staleTime: Duration.ofDays(1).toMillis(),
            retryDelay: (attemptIndex, _error) =>
                Math.min(
                    // 2 seconds + jitter
                    1000 * 2 ** attemptIndex + Math.random() * JITTER_MS,
                    // Up to a max of 5 minutes between retries
                    1000 * 60 * 5,
                ),
            retry: shouldRetryTransportError,
        },
    },
};

@injectable()
export class ThinkAlphaQueryClientImpl extends QueryClient {
    constructor() {
        super(QueryClientOptions);
    }
}
